import { Component, inject, linkedSignal, resource, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PrimengModule } from '@SharedModules/primeng.module';
import { TableDynamicComponent } from '@sharedComponents/table-dynamic/table-dynamic.component';
import { TableSkeletonComponent } from '@sharedComponents/table-skeleton/table-skeleton.component';
import { ModalBasicComponent } from '@sharedComponents/modal-basic/modal-basic.component';
import { MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { Router } from '@angular/router';
import { Column, FieldConfig } from '@sharedComponents/table-dynamic/interfaces';
import { Table, TableModule } from 'primeng/table';
import { MockService } from './mock.service';
import { AbstractControl, FormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActionButton } from '@sharedComponents/table-dynamic/interfaces/actions-buttons.interface';
import { AuthenticationService } from '@servicios/authentication.service';
import { Roles } from '@modelos/interfaces/operation-result-types';
import { debounceTime } from 'rxjs/operators';
import { CachedOptionsService } from '@servicios/cache/cachedOptions.service';
import { ConfigService } from '@servicios/config.service';
import { ButtonConfig } from '@sharedComponents/modal-template/modal-template.component';

@Component({
  selector: 'table-test',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    FormsModule,
    PrimengModule,
    TableDynamicComponent,
    ModalBasicComponent,
    TableSkeletonComponent
  ],
  templateUrl: './table-test.component.html',
  styleUrl: './table-test.component.scss',
  providers: [
    MessageService, 
    ConfirmationService
  ]
})
export class TableTestComponent {

  // Servicios
  service = inject(MockService);
  authService = inject(AuthenticationService);
  cachedOptionsService = inject(CachedOptionsService);
  configService = inject(ConfigService);
  messageService = inject(MessageService);
  confirmationService = inject(ConfirmationService);
  router = inject(Router);

  items = signal<any[]>([]); // Items para la tabla
  cols = signal<Column[]>([]); // Definicion de columnas para la tabla
  fieldConfig = signal<FieldConfig[]>([]); // Definicion de campos para el form del modal

  selected = linkedSignal<any | null>(() => {
    const _selected = this.data.hasValue() ? this.data.value()![0] : null;
    return _selected;
  });
  dialog = signal(false); // Dialogo

  // Ordenamiento
  sortField = signal<string>('id'); // Campo de ordenamiento
  sortOrder = signal<number>(1); // Ordenamiento

  // Filtros
  filters = signal<any>({}); // Filtros
  searchTerm = signal<string>(''); // Busqueda global

  // Paginacion
  first = signal<number>(0); // Primer registro
  currentPage = signal<number>(1); // Pagina actual
  pageSize = signal<number>(5); // Registros por pagina
  totalRecords = signal<number>(0); // Total de registros

  // Tipos de edicion y nuevo
  editType = signal<'modal' | 'page'>('modal');
  newType = signal<'modal' | 'page'>('modal');

  // Cargado y botones de accion
  dataLoaded = signal<boolean>(false); // Cargado
  isLoadingButton = signal<boolean>(false);
  actionButtons = signal<ActionButton[]>([]); // Botones de accion
  buttonsConfig = signal<ButtonConfig[]>([]);
  
  // Options
  programmedTasksOptions = signal<any[]>([]);
  customerOptions = signal<any[]>([]);

  @ViewChild('dt') dt!: Table;

  // Resource, manejo de la data
  data = resource({
    request: () => ({
      page: this.currentPage(),
      limit: this.pageSize(),
      search: this.searchTerm(),
      filters: this.filters(),
      sortField: this.sortField(),
      sortOrder: this.sortOrder()
    }),
    loader: async ({ request }) => {
      try {
        const _data = await this.service.get({
          page: request.page,
          limit: request.limit,
          search: request.search,
          filters: request.filters,
          sortBy: request.sortField,
          sortOrder: request.sortOrder > 0 ? 'asc' : 'desc'
        });
        console.log('Loaded data:', _data);
        this.totalRecords.set(_data.total);
        return _data.response;
      } catch (error) {
        console.error('Error loading data:', error);
        this.messageService.add({
          severity: 'error', 
          summary: 'Error', 
          detail: 'No se pudieron cargar los datos'
        });
        return []; // Return an empty array as fallback
      }
    }
  });

  ngOnInit() {
    // Definicion de botones de la tabla
    this.actionButtons.set([
      {
        icon: 'pi pi-pencil',
        label: 'Editar',
        type: 'edit',
        roles: [Roles.ADMINISTRATIVE, Roles.ADMIN],
        action: (event: any) => this.customAction({actionType: 'edit', item: event})
      },
      {
        icon: 'pi pi-trash',
        label: 'Eliminar',
        type: 'delete',
        roles: [Roles.ADMINISTRATIVE, Roles.ADMIN],
        action: (event: any) => this.customAction(event)
      },
      {
        icon: 'pi pi-lock',
        label: 'Blanquear Contraseña',
        type: 'resetPassword',
        roles: [Roles.ADMINISTRATIVE, Roles.ADMIN],
        action: (event: any) => this.customAction(event)
      }
    ]);

    // Cargar la configuracion
    this.loadConfig();
    this.dataLoaded.set(true);
  }

  async loadOptions() {
    // this.customerOptions.set(await this.cachedOptionsService.getCachedData({
    //   query: Get_CustomerSupplier,
    //   variables: {},
    //   dataPath: 'customersSuppliers',
    //   tableName: 'customerSupplier',
    //   versionTableName: 'customerSupplierVersion',
    //   fetchPolicy: 'network-only'
    // }));

    console.log(`Config: ${JSON.stringify(this.configService.appSettings?.backend?.programmedTasksOpts?.tasks)}`);
    this.programmedTasksOptions.set(this.configService.appSettings?.backend?.programmedTasksOpts?.tasks);
  }

  // Se dispara al cambiar de pagina en la tabla
  onPageChange(event: any) {
    console.log('Page change event:', event);
    this.currentPage.set(event.page + 1);
    this.first.set(event.first);
    this.pageSize.set(event.rows);
    this.searchTerm.set(event.search);
    this.data.reload();
  }

  // Se dispara al cargar la data de la tabla
  onLazyLoad(event: any) {
    console.log('Lazy load event:', event);
  }

  // Se dispara al buscar en la tabla
  onSearch(event: any) {
    this.searchTerm.set(event.search);
    this.data.reload();
  }

  // Exportar a CSV
  exportCSV() {
    if (this.dt) {
      this.dt.exportCSV();
    }
  }

  // Cargar la configuracion de la tabla
  async loadConfig() {
    await this.loadOptions();

    // Definicion de botones del modal
    this.buttonsConfig.set([
      {
        label: 'Cancelar',
        icon: 'pi pi-times',
        type: 'cancel',
        class: '',
        roles: [],
        action: (event: any, form: any) => this.customAction({ actionType: 'cancel', item: form })
      },
      {
        label: 'Guardar',
        icon: 'pi pi-save',
        type: 'save',
        class: '',
        roles: [Roles.ADMINISTRATIVE, Roles.ADMIN],
        action: (event: any, form: any) => this.customAction({ actionType: 'save', item: form })
      }
    ]);

    this.cols.set([
      { field: 'name', safeKey: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
      { field: 'description', safeKey: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
      { field: 'schedule', safeKey: 'schedule', header: 'Horas programadas', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
    ]);

    this.fieldConfig.set([
      { 
        key: 'name', 
        safeKey: 'name',
        label: 'Tareas Programadas', 
        type: 'select', 
        visible: true, 
        editable: true,
        validators: [Validators.required],
        validationMessages: {
          required: 'El task es requerida',
        },
        options: this.programmedTasksOptions(), 
        optionLabel: 'name',
        optionValue: 'name',
      },
      { 
        key: 'description', 
        label: 'Descripción', 
        type: 'textarea', 
        visible: true, 
        editable: true,
        validators: [Validators.required],
        validationMessages: {
          required: 'La descripción es requerida',
        } 
      },
      { 
        key: 'schedule', 
        label: 'Horas programadas', 
        type: 'text', 
        visible: true, 
        editable: true,
        validators: [] 
      },
      { 
        key: 'active', 
        safeKey: 'active',
        label: 'Activo', 
        type: 'switch', 
        visible: true, 
        editable: true,
        validators: [] 
      },
    ]);

  }

  // Abre el modal para agregar un nuevo item
  openNew = () => {
    this.selected.set({
      code: '',
      name: '',
      prefix: '',
      currency: '',
      default: false,
    });
    if(this.newType() === 'modal') {
      this.dialog.set(true);
    } else {
      this.router.navigate(['/table-test/new']);
    }
  }

  // Cierra el modal
  hideDialog = () => {
    this.dialog.set(false);
    this.selected.set(null);
  }

  // Abre el modal para editar un item
  edit = (item: any) => {
    this.selected.set({ ...item });
    if(this.editType() === 'modal') {
      this.dialog.set(true);
    } else {
      this.router.navigate(['/table-test', item.id]);
    }
  }

  // Eliminar todos los items seleccionados
  deleteAll = async (event: any) => {
    console.log(`Delete all event: ${JSON.stringify(event)}`);
    if (!event?.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No hay elementos seleccionados para eliminar',
        life: 3000
      });
      return;
    }
  
    this.confirmationService.confirm({
      message: `¿Está seguro de eliminar los ${event.length} elementos seleccionados?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      accept: async () => {
        try {
          // Mostrar indicador de carga
          this.messageService.add({
            severity: 'info',
            summary: 'Eliminando...',
            detail: 'Procesando la eliminación de los elementos',
            life: 2000
          });
  
          // Obtener los IDs de los elementos a eliminar
          const idsToDelete = event.map((item: any) => [item.id, item.v_]);
          
          // Llamar al servicio para eliminar múltiples elementos
          //const result = await this.service.deleteMultiple(idsToDelete);
          
          // Mostrar mensaje de éxito
          // this.messageService.add({
          //   severity: 'success',
          //   summary: 'Éxito',
          //   detail: `Se eliminaron ${result.count} elementos correctamente`,
          //   life: 3000
          // });
          
          // Actualizar la tabla
          this.data.reload();
          
        } catch (error) {
          console.error('Error al eliminar elementos:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Ocurrió un error al eliminar los elementos seleccionados',
            life: 5000
          });
        }
      },
      reject: () => {
        // El usuario canceló la eliminación
        this.messageService.add({
          severity: 'info',
          summary: 'Operación cancelada',
          detail: 'No se eliminó ningún elemento',
          life: 3000
        });
      }
    });
  }

  // Eliminar un item
  delete = async (item: any) => {
    if (!this.confirmationService) {
      console.error('Confirmation service is not initialized');
      this.messageService.add({
        severity: 'error', 
        summary: 'Error', 
        detail: 'Confirmation service is not available'
      });
      return;
    }

    try {
      this.confirmationService.confirm({
        message: '¿Está seguro que desea eliminar?',
        header: 'Confirmar',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sí',
        rejectLabel: 'No',
        accept: async () => {
          try {
            const _delete = await this.service.delete(item.id!, item.v_);
            if(_delete) {
              this.messageService.add({
                severity: 'success', 
                summary: 'Éxito', 
                detail: 'Eliminado'
              });
              this.data.reload();
            }
          } catch (error) {
            console.error('Error deleting:', error);
            this.messageService.add({
              severity: 'error', 
              summary: 'Error', 
              detail: 'No se pudo eliminar'
            });
          }
        }
      });
    } catch (error) {
      console.error('Error in confirmationService.confirm:', error);
      this.messageService.add({
        severity: 'error', 
        summary: 'Error', 
        detail: 'No se pudo iniciar la confirmación de eliminación'
      });
    }
  }

  // Guardar un item
  save = async (item: any) => {
    if (!this.selected()) return;
    
        try {
          if (!this.selected()?.id) {
            const created = await this.service.add(item);
            if(created){
              this.messageService.add({
                severity: 'success', 
                summary: 'Éxito', 
                detail: 'Creado'
              });
            } 
          } else {
            // Update existing
            const updated = await this.service.update(this.selected()!.id!, item);
            if(updated){
              this.messageService.add({
                severity: 'success', 
                summary: 'Éxito', 
                detail: 'Actualizado'
              });
            }
          }
    
          this.dialog.set(false);
          this.selected.set(null);
          this.data.reload();
        } catch (error) {
          this.messageService.add({
            severity: 'error', 
            summary: 'Error', 
            detail: 'No se pudo guardar'
          });
        }
  }

  // Validaciones Asyncronas
  codeExists(control: AbstractControl): Promise<ValidationErrors | null> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Simulamos una verificación de código existente
        if (control.value === 'TEST') {
          resolve({ codeExists: true });
        } else {
          resolve(null);
        }
      }, 1000);
    });
  }

  // Accion Custom para agregar en el menu de la tabla
  customAction(item: any) {
    const { item: _item, actionType } = item;
    if (!this.confirmationService) {
      console.error('Confirmation service is not initialized');
      this.messageService.add({
        severity: 'error', 
        summary: 'Error', 
        detail: 'Confirmation service is not available'
      });
      return;
    }

    if(actionType === 'edit') {
      this.edit(_item);
      return;
    }

    if(actionType === 'delete') {
      this.delete(_item);
      return;
    }

    // Ejemplo:
    // if(actionType === 'resetPassword') {
    //   try {
    //     this.confirmationService.confirm({
    //       message: '¿Está seguro que desea restablecer la contraseña de este usuario?',
    //       header: 'Confirmar',
    //       icon: 'pi pi-exclamation-triangle',
    //       acceptLabel: 'Restablecer',
    //       rejectLabel: 'Cancelar',
    //       accept: async () => {
    //         try {
    //           const passwordReset = await this.service.resetPassword(_item.id!);
    //           if(passwordReset) {
    //             this.messageService.add({
    //               severity: 'success', 
    //               summary: 'Éxito', 
    //               detail: 'Contraseña restablecida'
    //             });
    //             this.data.reload();
    //           }
    //         } catch (error) {
    //           console.error('Error restableciendo la contraseña:', error);
    //           this.messageService.add({
    //             severity: 'error', 
    //             summary: 'Error', 
    //             detail: 'No se pudo restablecer la contraseña'
    //           });
    //         }
    //       }
    //     });
    //   } catch (error) {
    //     console.error('Error in confirmationService.confirm:', error);
    //     this.messageService.add({
    //       severity: 'error', 
    //       summary: 'Error', 
    //       detail: 'No se pudo iniciar la confirmación de restablecimiento de contraseña'
    //     });
    //   } 
    // }
  }
    
}
